import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { QuotaSummaryBarComponent } from '../../../../shared/components/quota-summary-bar/quota-summary-bar';
import { QuotaService } from '../../../quotas/services/quota.service';
import { AuthService } from '../../../../core/services/auth.service';
import { ConclusionVolume } from '../../../quotas/models/conclusion-volume.interface';
import { KgToTonsPipe } from '../../../../shared/pipes/kg-to-tons.pipe';
import { AddVolumeDialogComponent } from '../../components/add-volume-dialog/add-volume-dialog.component';
import { RevokeQuotaDialogComponent } from '../../components/revoke-quota-dialog/revoke-quota-dialog.component';
import { AddConclusionVolumeDialogComponent } from '../../components/add-conclusion-volume-dialog/add-conclusion-volume-dialog.component';
import { UpdateDeliveredVolumeDialogComponent } from '../../components/update-delivered-volume-dialog/update-delivered-volume-dialog.component';
import { EditConclusionVolumeDialogComponent } from '../../components/edit-conclusion-volume-dialog/edit-conclusion-volume-dialog.component';

@Component({
  selector: 'app-past-suppliers',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatMenuModule,
    MatDividerModule,
    MatSnackBarModule,
    QuotaSummaryBarComponent,
    KgToTonsPipe,
    TranslateModule
  ],
  templateUrl: './past-suppliers.html',
  styleUrl: './past-suppliers.css',
})
export class PastSuppliers implements OnInit {
  private quotaService = inject(QuotaService);
  private authService = inject(AuthService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private translate = inject(TranslateService);

  loading = signal(false);
  error = signal<string | null>(null);
  activeTab = signal('all');
  conclusionVolumes = signal<ConclusionVolume[]>([]);
  tabCounts = signal<Record<string, number>>({});

  // Табы без "изъятые"
  tabs = [
    { value: 'all', labelKey: 'pastSuppliers.tabs.all' },
    { value: 'beef', labelKey: 'pastSuppliers.tabs.beef' },
    { value: 'chicken', labelKey: 'pastSuppliers.tabs.chicken' },
    { value: 'pork', labelKey: 'pastSuppliers.tabs.pork' }
  ];

  // Проверка роли - quota_manager может только просматривать
  isManager = computed(() => {
    return this.authService.hasRole('quota_viewer') || (this.authService.hasRole('quota_manager') && !this.authService.hasRole('quota_operator'));
  });

  displayedColumns = computed(() => {
    const columns = [
      'number',
      'year',
      'applicantName',
      'distributedVolume',
      'deliveredVolume',
      'remainingVolume',
      'meatType',
      'status'
    ];

    // Добавляем колонку действий только если НЕ менеджер
    if (!this.isManager()) {
      columns.push('actions');
    }

    return columns;
  });

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading.set(true);
    this.error.set(null);

    this.quotaService.getConclusionVolumes({ page: 1, size: 1000 }).subscribe({
      next: (response) => {
        if (response.success) {
          let volumes = response.res.content || [];
          const meatTypeCounts = response.res.meatTypeCounts;

          // Устанавливаем счетчики для табов
          this.tabCounts.set({
            all: meatTypeCounts.totalCount,
            beef: meatTypeCounts.beefCount,
            chicken: meatTypeCounts.chickenCount,
            pork: meatTypeCounts.porkCount
          });

          // Фильтруем по активному табу
          const activeTab = this.activeTab();
          if (activeTab !== 'all') {
            volumes = volumes.filter(v =>
              v.meatType.alias.toLowerCase() === activeTab
            );
          }

          this.conclusionVolumes.set(volumes);
        } else {
          this.error.set(response.msg || this.translate.instant('pastSuppliers.errors.loadingData'));
        }
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading conclusion volumes:', err);
        this.error.set(this.translate.instant('pastSuppliers.errors.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  setActiveTab(tab: string): void {
    this.activeTab.set(tab);
    this.loadData();
  }

  getActiveTabLabel(): string {
    const tab = this.tabs.find(t => t.value === this.activeTab());
    return tab ? this.translate.instant(tab.labelKey) : '';
  }

  getTabCount(tabValue: string): number {
    return this.tabCounts()[tabValue] || 0;
  }

  getStatusLabel(status: string): string {
    const statusKeys: Record<string, string> = {
      'IN_PROGRESS': 'pastSuppliers.statuses.inProgress',
      'COMPLETED': 'pastSuppliers.statuses.completed',
      'CANCELLED': 'pastSuppliers.statuses.cancelled'
    };
    return statusKeys[status] ? this.translate.instant(statusKeys[status]) : status;
  }

  addVolume(volume: ConclusionVolume): void {
    const dialogRef = this.dialog.open(UpdateDeliveredVolumeDialogComponent, {
      width: '600px',
      disableClose: true,
      data: { volume }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        // Перезагрузить данные после успешного обновления объема
        this.loadData();
      }
    });
  }

  revokeQuota(volume: ConclusionVolume): void {
    const dialogRef = this.dialog.open(EditConclusionVolumeDialogComponent, {
      width: '600px',
      disableClose: true,
      data: { volume }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        // Перезагрузить данные после успешного редактирования
        this.loadData();
      }
    });
  }

  openAddDialog(): void {
    const dialogRef = this.dialog.open(AddConclusionVolumeDialogComponent, {
      width: '600px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        // Перезагрузить данные после успешного добавления
        this.loadData();
      }
    });
  }

  getMeatTypeName(meatType: any): string {
    if (!meatType) return '';
    const lang = this.translate.currentLang || 'ru';
    return lang === 'ky' ? meatType.nameKy : meatType.nameRu;
  }
}
